import { seedPricing, urgencyFeeCents, urgencyMode } from '@rpl/pricing';
import { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatMoney } from '../../lib/format';
import { pricedPrints } from '../../lib/sizePaper';
import { track } from '../../lib/analytics';
import { smallPrintsOffer, type SmallPrintsOffer } from '../../lib/upsell';
import { reportUpsell } from '../../lib/upsellReport';
import { slowestTurnaround } from '../../lib/turnaround';
import { useCartExtras } from '../../store/cartExtras';
import { toPrintSelections, useStore, useTotals } from '../../store/store';
import { BulkNudge } from '../cart/BulkNudge';
import { FreeShippingNudge } from '../cart/FreeShippingNudge';
import { keyOfGroup, groupMembers } from '../cart/cartModel';
import { GroupCard } from '../cart/GroupCard';
import { BlockedBanner, NoticeBanner } from '../cart/Notices';
import { SmallPrintsSheet } from '../cart/SmallPrintsSheet';
import { SaveOrderModal } from '../cart/SaveOrderModal';
import { OrderNotesDialog } from '../cart/OrderNotesDialog';
import { TurnaroundDialog } from '../cart/TurnaroundDialog';
import { SummaryPanel } from '../cart/SummaryPanel';
import { UrgencyDialog } from '../cart/UrgencyDialog';
import { Chrome } from '../chrome/Chrome';
import { CropEditor } from '../crop/CropEditor';
import { useOrderActions } from '../sizepaper/useOrderActions';
import '../cart/cart.css';

/**
 * STEP 3 — Cart (screen 1e): order review grouped by size+paper (loose) and by
 * framed product, driven by useTotals().groups. Quantity steppers, frame
 * upsell, bulk-pricing nudges, save-for-later, and the checkout kick-off.
 */
export function CartScreen() {
  const setStep = useStore((s) => s.setStep);
  const prints = useStore((s) => s.prints);
  const photos = useStore((s) => s.photos);
  const pricing = useStore((s) => s.pricing) ?? seedPricing;
  const totals = useTotals();
  const isMobile = useIsMobile();

  const phase = useCartExtras((s) => s.phase);
  const startCheckout = useCartExtras((s) => s.startCheckout);
  const hydrateSavedEmail = useCartExtras((s) => s.hydrateSavedEmail);
  const [saveOpen, setSaveOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [urgencyOpen, setUrgencyOpen] = useState(false);
  /* Frame-upsell low-res handoff. This state lives HERE, not in GroupCard:
     converting a whole loose line drops its group from totals.groups, so the
     card that owns the frame dialog unmounts the moment the conversion
     lands (owner 2026-07-27). */
  const cropId = useStore((s) => s.cartCropId);
  const setCropId = useStore((s) => s.setCartCropId);
  const actions = useOrderActions();
  const orderNote = useStore((s) => s.orderNote);
  const urgent = useStore((s) => s.urgent);

  // Resume-after-save support: learn the project's stored email once.
  useEffect(() => {
    void hydrateSavedEmail();
  }, [hydrateSavedEmail]);

  const photosById = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos]);
  const cropPrint = cropId ? prints.find((p) => p.id === cropId) : undefined;
  const cropPhoto = cropPrint ? photos.find((p) => p.id === cropPrint.photoId) : undefined;
  const selections = useMemo(() => toPrintSelections(prints), [prints]);

  const subtotal = totals?.subtotal ?? 0;
  const printCount = totals?.printCount ?? 0;
  const checkoutBusy = phase === 'submitting';
  const empty = !totals || totals.groups.length === 0;

  // Urgent-processing upsell (gated on the live pricing block). Mode comes from
  // the priced prints (same set the totals use); the fee follows the subtotal.
  const urgencyEnabled = !!pricing.urgency?.enabled;
  const urgencyFee = urgencyEnabled ? urgencyFeeCents(pricing, subtotal) / 100 : 0;
  const urgencyModeLive = useMemo(
    () => urgencyMode(toPrintSelections(pricedPrints(prints))),
    [prints],
  );
  const urgencyActive = urgencyEnabled && urgent;
  // Paid mode folds the fee into the shown total; request mode leaves it out.
  const displayTotal = subtotal + (urgencyActive && urgencyModeLive === 'paid' ? urgencyFee : 0);

  // Pre-checkout turnaround dialog (owner 2026-07-31): one line, the slowest
  // family in the cart, once per session, and its button IS the checkout
  // trigger — never dismiss-then-click-again. Older pricing without the
  // turnaroundDays block skips straight to checkout, exactly as before.
  const turnaroundSeen = useCartExtras((s) => s.turnaroundSeen);
  const markTurnaroundSeen = useCartExtras((s) => s.markTurnaroundSeen);
  const [turnOpen, setTurnOpen] = useState(false);
  const turnRange = useMemo(() => slowestTurnaround(pricing, prints), [pricing, prints]);
  /* Cart-exit small-prints sheet (owner 2026-08-17): intercepts the FIRST
     Checkout tap, same pattern as the turnaround dialog below — the sheet's
     buttons ARE the way forward, never dismiss-then-click-again. Wizard-built
     orders and repeat taps skip straight through. */
  const [spOffer, setSpOffer] = useState<SmallPrintsOffer | null>(null);
  const proceedToCheckout = () => {
    if (turnRange && !turnaroundSeen) {
      track('turnaround_shown', { min: turnRange.minDays, max: turnRange.maxDays });
      setTurnOpen(true);
      return;
    }
    void startCheckout();
  };
  const checkout = () => {
    const st = useStore.getState();
    if (!st.smallPrintsSeen && !st.wizardApplied) {
      // Duplicates only — the unprinted-photos case is handled earlier by the
      // no-prints dialog's offer variant, so nobody is asked twice.
      const offer = smallPrintsOffer(
        pricing,
        actions.catalog,
        photos,
        prints,
        actions.thresholds,
        subtotal,
        'duplicates',
      );
      if (offer) {
        st.setSmallPrintsSeen(true);
        reportUpsell('shown', 'cart_smallprints', offer.items[0]!.sizeId);
        setSpOffer(offer);
        return;
      }
      st.setSmallPrintsSeen(true); // nothing to offer — never re-evaluate
    }
    proceedToCheckout();
  };
  const acceptSmallPrints = (paper: import('@rpl/pricing').PaperId) => {
    if (!spOffer) return;
    reportUpsell('accepted', 'cart_smallprints', spOffer.items[0]!.sizeId);
    for (const it of spOffer.items) {
      // C-Type at the sheet's chosen finish (lustre default), edge border:
      // no border gate, and the ratio fit guarantees no crop/low-res flags.
      actions.ensurePrint(it.photoId, it.sizeId, paper, 'none');
    }
    setSpOffer(null);
    proceedToCheckout();
  };
  const skipSmallPrints = () => {
    if (spOffer) reportUpsell('dismissed', 'cart_smallprints', spOffer.items[0]!.sizeId);
    setSpOffer(null);
    proceedToCheckout();
  };
  const confirmTurnaround = () => {
    markTurnaroundSeen();
    setTurnOpen(false);
    void startCheckout();
  };

  const body = (
    <div className="cart-body">
      <div className="cart-main">
        <h2 className="cart-title">
          {/* mobile-only (CSS-gated): the header row is gone on phones and the
              mobile footer has no secondary action — this is the way back */}
          <button
            type="button"
            className="pane__back"
            onClick={() => setStep(1)}
            aria-label="Back to sizes"
          >
            ‹
          </button>
          Your cart
        </h2>
        <BlockedBanner prints={prints} photos={photos} pricing={pricing} />
        <NoticeBanner />
        {empty ? (
          <div className="cart-empty">
            <div className="cart-empty__title">Nothing in your cart yet</div>
            <div className="cart-empty__body">
              Head back to Size &amp; Paper, pick some photos and add print sizes. They&rsquo;ll
              group up here.
            </div>
            <button type="button" className="btn btn--primary" onClick={() => setStep(1)}>
              Choose sizes →
            </button>
          </div>
        ) : (
          <>
            {totals.groups.map((g) => (
              <GroupCard
                key={keyOfGroup(g)}
                group={g}
                prints={groupMembers(prints, g)}
                photosById={photosById}
                pricing={pricing}
                onReview={setCropId}
              />
            ))}
            <BulkNudge pricing={pricing} selections={selections} />
            <FreeShippingNudge pricing={pricing} prints={prints} subtotal={subtotal} />
            {orderNote ? (
              <div className="cart-notes cart-notes--set">
                <div className="cart-notes__eyebrow eyebrow">Instructions for the lab</div>
                <div className="cart-notes__text">{orderNote}</div>
                <button type="button" className="linkbtn" onClick={() => setNotesOpen(true)}>
                  ✎ Edit instructions
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn--secondary cart-notes__add"
                onClick={() => setNotesOpen(true)}
              >
                ＋ Add Instructions
                <span className="cart-notes__hint">e.g. “No colour correction”</span>
              </button>
            )}
          </>
        )}
      </div>
      <aside className="cart-aside">
        {totals && (
          <SummaryPanel
            totals={totals}
            pricing={pricing}
            urgency={urgencyActive ? { mode: urgencyModeLive, fee: urgencyFee } : null}
            urgencyUpsell={
              urgencyEnabled
                ? {
                    active: urgent,
                    label: pricing.urgency?.label ?? 'Urgent processing',
                    fee: urgencyFee,
                    mode: urgencyModeLive,
                    onOpen: () => setUrgencyOpen(true),
                  }
                : null
            }
            onCheckout={checkout}
            onSave={() => setSaveOpen(true)}
            checkoutBusy={checkoutBusy}
            mobile={isMobile}
          />
        )}
      </aside>
    </div>
  );

  return (
    <Chrome
      step={2}
      headerRight={
        <>
          <button type="button" className="linkbtn linkbtn--dark" onClick={() => setSaveOpen(true)}>
            Save my order
          </button>
          <span>
            Total <strong>{formatMoney(displayTotal)}</strong>
          </span>
        </>
      }
      footer={{
        status: empty
          ? 'Your cart is empty'
          : `${printCount} ${printCount === 1 ? 'print' : 'prints'} · GST included`,
        secondary: { label: '← Back to sizes', onClick: () => setStep(1) },
        primary: {
          label: checkoutBusy ? 'Preparing…' : 'Checkout →',
          onClick: checkout,
          disabled: empty || checkoutBusy,
        },
      }}
      mobileFooter={{
        totalLabel: 'Total',
        totalValue: formatMoney(displayTotal),
        primary: {
          label: checkoutBusy ? 'Preparing…' : 'Checkout →',
          onClick: checkout,
          disabled: empty || checkoutBusy,
        },
      }}
    >
      {body}
      {saveOpen && <SaveOrderModal onClose={() => setSaveOpen(false)} />}
      {spOffer && (
        <SmallPrintsSheet
          offer={spOffer}
          photosById={new Map(photos.map((ph) => [ph.id, ph]))}
          onAdd={acceptSmallPrints}
          onSkip={skipSmallPrints}
        />
      )}
      {turnOpen && turnRange && (
        <TurnaroundDialog
          range={turnRange}
          onConfirm={confirmTurnaround}
          onClose={() => setTurnOpen(false)}
        />
      )}
      {notesOpen && <OrderNotesDialog pricing={pricing} onClose={() => setNotesOpen(false)} />}
      {urgencyOpen && <UrgencyDialog pricing={pricing} onClose={() => setUrgencyOpen(false)} />}
      {cropPrint && cropPhoto && (
        <CropEditor
          key={cropPrint.id}
          print={cropPrint}
          photo={cropPhoto}
          pricing={pricing}
          catalog={actions.catalog}
          actions={actions}
          onClose={() => setCropId(null)}
        />
      )}
    </Chrome>
  );
}
